import React, { useRef, useEffect, useState } from "react";
import {
    Formik,
    FormikProps,
    useFormikContext,
    getIn,
} from "formik";
import { clientInvInf } from "../../redux-store/types/api-types";
import { clientInvInfrmationSchema } from "../../utils/schema";
import {
    Box,
    Grid,
    useTheme,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Tooltip,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import { ProceedSaveLater, SubHeading } from "./components";
import { MFTextField } from "../../lib/formik";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MobileInvestmentFormCard from "../investorOverview/viewProfile/MobileClientEdit";
import { useHistory, useRouteMatch } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { updateApplication } from "../../redux-store/actions/application";
import { showError } from "../../redux-store/actions/auth";
import { useSnackbar } from "notistack";
import {
    applicationComparison,
    saveForLater,
    sendRoleBasedStatus,
} from "../../utils/utilityFunctions";
import { ONBOARDING_DEMAT_DETAILS } from "../../utils/constant";
import { updateClientInvestment } from "../../redux-store/actions/application";
import { LoadingButton } from "@mui/lab";

type Values = {
    status?: string;
    applicationType?: string;
    clientInvInf: clientInvInf;
    saveType: string;
};

const clientInvInfInitialValues = {
    investmentPortfolio: {
        mutualFund: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        fixedDeposit: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        bond: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        directEquity: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        pms: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        aif: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        realEstate: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        otherInvestment: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
        totalInvestment: {
            totalInvestment: '',
            investmentThroughTCW: '',
            otherWealthBank: '',
            remarks: ''
        },
    },
    protectionCoverage: {
        lifeInsurance: {
            totalSumInsured: '',
            annualPremium: '',
        },
        generalInsurance: {
            totalSumInsured: '',
            annualPremium: '',
        },
    },
    financing: {
        homeLoan: {
            totalLoanAmount: '',
            totalOutstanding: '',
            monthlyEmi: '',
            remarks: ''
        },
        loanAgainstSecurities: {
            totalLoanAmount: '',
            totalOutstanding: '',
            monthlyEmi: '',
            remarks: ''
        },
        homeEquity: {
            totalLoanAmount: '',
            totalOutstanding: '',
            monthlyEmi: '',
            remarks: ''
        },
        otherLoan: {
            totalLoanAmount: '',
            totalOutstanding: '',
            monthlyEmi: '',
            remarks: ''
        },
        totalLoan: {
            totalLoanAmount: '',
            totalOutstanding: '',
            monthlyEmi: '',
            remarks: ''
        }
    }
}
//4&2 columns for investment portfolio and financing
const initialValues: Values = {
    status: '',
    applicationType: '',
    clientInvInf: clientInvInfInitialValues,
    saveType: 'save and proceed',
};

const GRID_COLUMNS = { xs: 12, md: 24 };
const GRID_INSTRUMENT = { xs: 12, sm: 12, md: 6 };
const GRID_VALUE = { xs: 12, sm: 6, md: 5 };
const GRID_REMARKS = { xs: 12, sm: 6, md: 3 };
const GRID_REMARKS_TOTAL = {
    xs: 12,
    sm: 6,
    md: GRID_VALUE.md + GRID_REMARKS.md,
};

const COMMON_GRID_CONFIG = {
    columns: GRID_COLUMNS,
    instrument: GRID_INSTRUMENT,
    col: GRID_VALUE,
    remarks: GRID_REMARKS,
    remarksTotal: GRID_REMARKS_TOTAL,
};

const DESKTOP_COLUMN_SPACING = { xs: 1, md: 2.5 };


const PROTECTION_GRID_CONFIG = {
    instrument: { xs: 12, sm: 12, md: 3 },
    col: { xs: 12, sm: 6, md: 4.5 },
};

const COMMON_STYLES = {
    header: {
        fontWeight: 600,
        paddingLeft: { xs: 0, sm: 1 },
        paddingRight: { xs: 0, sm: 1 },
        mb: "8px",
        justifyContent: "space-between",
        display: "flex",
        alignItems: "center",
    },
    row: {
        borderTop: "1px solid rgba(0,0,0,0.06)",
        alignItems: "stretch",
        minHeight: { xs: "auto", sm: "72px" },
        display: "flex",
        flexWrap: "wrap",
    },
    cell: {
        padding: { xs: "8px 4px", sm: "6px" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        minHeight: { xs: "auto", sm: "56px" },
    },
    instrumentCell: {
        padding: { xs: "8px 4px", sm: "8px" },
        display: "flex",
        alignItems: "center",
        minHeight: { xs: "auto", sm: "56px" },
        fontWeight: { xs: 500, sm: 400 },
    },
    remarksCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "flex-end" },
        height: "100%",
        padding: { xs: "8px 4px", sm: "6px" },
        minHeight: { xs: "auto", sm: "56px" },
    },
    remarksHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "flex-end" },
        textAlign: { xs: "left", sm: "right" },
    },
    remarksButton: {
        flexShrink: 0,
        transition: "color 0.2s ease-in-out",
    },
    remarksButtonHasValue: {
        color: "#ffb74d",
    },
    totalRowField: {
        '& .MuiInputBase-root': {
            backgroundColor: '#f5f5f5',
            '&.Mui-focused': {
                backgroundColor: '#f5f5f5',
            },
            '& .MuiInputBase-input': {
                cursor: 'default',
                color: 'text.secondary',
            }
        }
    },
    formControl: {
        width: "100%",
    },
};

const formatSumValue = (value: number): string => {
    if (!value) {
        return "";
    }

    const fixed = value.toFixed(5);
    const [intPart, decimalPart = ""] = fixed.split(".");

    if (!decimalPart) {
        return intPart;
    }

    return `${intPart}.${decimalPart}`;
};

// Memoize validation schema so it's not recreated on every render
const clientInvInfrmationValidationSchema = clientInvInfrmationSchema();

function InvestmentPortfolio(): JSX.Element {
    const { values, setFieldValue, errors, touched } = useFormikContext<Values>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [remarksOpen, setRemarksOpen] = useState(false);
    const [remarksPath, setRemarksPath] = useState<string | null>(null);
    const [remarksRowLabel, setRemarksRowLabel] = useState<string>("");
    const [remarksDraft, setRemarksDraft] = useState<string>("");

    const rows = [
        { key: "mutualFund", label: "Mutual Funds" },
        { key: "fixedDeposit", label: "Fixed Deposits (Corp/Bank)" },
        { key: "bond", label: "Bonds / NCDs / Pref shares etc" },
        { key: "directEquity", label: "Direct Equities (Stocks)" },
        { key: "pms", label: "PMS" },
        { key: "aif", label: "AIFs/ Structures" },
        { key: "realEstate", label: "Real Estate" },
        { key: "otherInvestment", label: "Others" },
        { key: "totalInvestment", label: "Total Amount" },
    ];

    const cols = [
        { key: "totalInvestment", label: "Total Investments (Rs. in Lakhs)", numeric: true },
        { key: "investmentThroughTCW", label: "Investment through Aditya Birla (Rs. in Lakhs)", numeric: true },
        { key: "otherWealthBank", label: "Name of other wealth houses / bank", numeric: false },
        { key: "remarks", label: "Remarks", numeric: false },
    ];

    //get value
    const getValue = (section: string, col: string) =>
        (values as any)?.clientInvInf?.investmentPortfolio?.[section]?.[col] ?? "";

    const handleChange = (section: string, col: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const path = `clientInvInf.investmentPortfolio.${section}.${col}`;
        setFieldValue(path, e.target.value);
    };

    //remarks clickopen
    const openRemarks = (sectionKey: string, rowLabel: string) => {
        const path = `clientInvInf.investmentPortfolio.${sectionKey}.remarks`;
        const current = getValue(sectionKey, "remarks") ?? "";
        setRemarksPath(path);
        setRemarksRowLabel(rowLabel);
        setRemarksDraft(current);
        setRemarksOpen(true);
    };

    const closeRemarks = () => {
        setRemarksOpen(false);
        setRemarksPath(null);
        setRemarksDraft("");
    };

    const onRemarksOkay = () => {
        if (remarksPath) {
            setFieldValue(remarksPath, remarksDraft);
        }
        closeRemarks();
    };

    // Auto-calc totals with guard to avoid unnecessary setFieldValue calls
    useEffect(() => {
        const inv = (values as any)?.clientInvInf?.investmentPortfolio;
        if (!inv) return;

        const numericCols = ["totalInvestment", "investmentThroughTCW"];
        const sums: Record<string, number> = { totalInvestment: 0, investmentThroughTCW: 0 };

        rows
            .filter((r) => r.key !== "totalInvestment")
            .forEach((row) => {
                numericCols.forEach((col) => {
                    const raw = inv[row.key]?.[col];
                    const num = raw === "" || raw === null || raw === undefined ? 0 : Number(raw);
                    if (!isNaN(num)) sums[col] += num;
                });
            });

        const newTotalInvestment = sums.totalInvestment ? formatSumValue(sums.totalInvestment) : "";
        const newInvestmentThroughTCW = sums.investmentThroughTCW ? formatSumValue(sums.investmentThroughTCW) : "";

        const currentTotalInvestment =
            inv?.totalInvestment?.totalInvestment ?? "";
        const currentInvestmentThroughTCW =
            inv?.totalInvestment?.investmentThroughTCW ?? "";

        if (currentTotalInvestment !== newTotalInvestment) {
            setFieldValue(
                "clientInvInf.investmentPortfolio.totalInvestment.totalInvestment",
                newTotalInvestment
            );
        }

        if (currentInvestmentThroughTCW !== newInvestmentThroughTCW) {
            setFieldValue(
                "clientInvInf.investmentPortfolio.totalInvestment.investmentThroughTCW",
                newInvestmentThroughTCW
            );
        }
    }, [
        //calclulating the totals
        (values as any)?.clientInvInf?.investmentPortfolio?.mutualFund?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.fixedDeposit?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.bond?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.directEquity?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.pms?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.aif?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.realEstate?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.otherInvestment?.totalInvestment,
        (values as any)?.clientInvInf?.investmentPortfolio?.mutualFund?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.fixedDeposit?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.bond?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.directEquity?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.pms?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.aif?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.realEstate?.investmentThroughTCW,
        (values as any)?.clientInvInf?.investmentPortfolio?.otherInvestment?.investmentThroughTCW,
        setFieldValue,
    ]);

    return (
        <>
            {/* //mobileview */}
            {isMobile ? (
                <Box sx={{ marginTop: 2, width: '100%' }}>
                    {rows.map((r) => {
                        const isTotalRow = r.key === "totalInvestment";
                        const fields = cols
                            .filter((c) => c.key !== 'remarks')
                            .map((c) => {
                                const namePath = `clientInvInf.investmentPortfolio.${r.key}.${c.key}`;
                                const error = getIn(errors, namePath);
                                const wasTouched = getIn(touched, namePath);
                                const showError = Boolean(error && wasTouched);

                                return {
                                    key: c.key,
                                    label: c.label,
                                    namePath,
                                    value: getValue(r.key, c.key),
                                    onChange: handleChange(r.key, c.key),
                                    error: showError,
                                    numeric: c.numeric,
                                    readOnly: isTotalRow,
                                    placeholder: c.label,
                                };
                            });

                        const remarksValue = getValue(r.key, 'remarks') ?? '';
                        const remarksNamePath = `clientInvInf.investmentPortfolio.${r.key}.remarks`;
                        const remarksError = Boolean(getIn(errors, remarksNamePath) && getIn(touched, remarksNamePath));

                        return (
                            <MobileInvestmentFormCard
                                key={r.key}
                                instrument={r.label}
                                fields={fields}
                                remarks={remarksValue}
                                remarksError={remarksError}
                                onRemarksClick={() => openRemarks(r.key, r.label)}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Grid container spacing={1} sx={{ marginTop: 1 }}>
                    <Grid
                        container
                        item
                        xs={12}
                        spacing={1}
                        columnSpacing={DESKTOP_COLUMN_SPACING}
                        columns={COMMON_GRID_CONFIG.columns}
                        sx={COMMON_STYLES.header}
                    >
                        <Grid item {...COMMON_GRID_CONFIG.instrument} sx={COMMON_STYLES.header}>
                            Instruments
                        </Grid>
                        {cols.map((c) => (
                            <Grid
                                item
                                key={c.key}
                                {...(c.key === "remarks" ? COMMON_GRID_CONFIG.remarks : COMMON_GRID_CONFIG.col)}
                                sx={{
                                    ...COMMON_STYLES.header,
                                    ...(c.key === "remarks" ? COMMON_STYLES.remarksHeader : {}),
                                }}
                            >
                                {c.label}
                            </Grid>
                        ))}
                    </Grid>

                    {rows.map((r) => (
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={1}
                            columnSpacing={DESKTOP_COLUMN_SPACING}
                            columns={COMMON_GRID_CONFIG.columns}
                            key={r.key}
                            sx={COMMON_STYLES.row}
                        >
                            <Grid item {...COMMON_GRID_CONFIG.instrument} sx={COMMON_STYLES.instrumentCell}>
                                {r.label}
                            </Grid>

                            {cols.map((c) => {
                                const namePath = `clientInvInf.investmentPortfolio.${r.key}.${c.key}`;
                                const error = getIn(errors, namePath);
                                const wasTouched = getIn(touched, namePath);
                                const showError = Boolean(error && wasTouched);

                                const isTotalRow = r.key === "totalInvestment";
                                const isRemarksCol = c.key === "remarks";
                                const remarksGridConfig = isTotalRow
                                    ? COMMON_GRID_CONFIG.remarksTotal
                                    : COMMON_GRID_CONFIG.remarks;

                                if (isRemarksCol) {
                                    const currentRemark = getValue(r.key, c.key) ?? "";
                                    const hasRemark = Boolean(currentRemark.trim());

                                    return (
                                        <Grid
                                            item
                                            key={c.key}
                                            {...remarksGridConfig}
                                            sx={COMMON_STYLES.remarksCell}
                                        >
                                            <Tooltip
                                                title={hasRemark ? "Edit remark" : "Add a remark"}
                                                placement="top"
                                                arrow
                                            >
                                                <span>
                                                    <IconButton
                                                        aria-label={hasRemark ? `edit-remark-${r.key}` : `add-remark-${r.key}`}
                                                        size="small"
                                                        onClick={() => openRemarks(r.key, r.label)}
                                                        sx={{
                                                            ...COMMON_STYLES.remarksButton,
                                                            ...(hasRemark ? COMMON_STYLES.remarksButtonHasValue : {})
                                                        }}
                                                    >
                                                        {hasRemark ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Grid>
                                    );
                                }


                                if (isTotalRow && c.key === "otherWealthBank") {
                                    return null;
                                }

                                if (c.numeric) {
                                    return (
                                        <Grid
                                            item
                                            key={c.key}
                                            {...COMMON_GRID_CONFIG.col}
                                            sx={COMMON_STYLES.cell}
                                        >
                                            <FormControl
                                                fullWidth
                                                error={showError}
                                                variant="standard"
                                                sx={COMMON_STYLES.formControl}
                                            >
                                                <MFTextField
                                                    name={namePath}
                                                    value={getValue(r.key, c.key)}
                                                    onChange={handleChange(r.key, c.key)}
                                                    size="small"
                                                    fullWidth
                                                    placeholder={c.label}
                                                    error={showError}
                                                    inputProps={isTotalRow ? { readOnly: true } : { inputMode: "numeric", pattern: "[0-9]*" }}
                                                    sx={isTotalRow ? COMMON_STYLES.totalRowField : {}}
                                                />
                                            </FormControl>
                                        </Grid>
                                    );
                                }
                                return (
                                    <Grid
                                        item
                                        key={c.key}
                                        {...(c.key === "remarks" ? remarksGridConfig : COMMON_GRID_CONFIG.col)}
                                        sx={COMMON_STYLES.cell}
                                    >
                                        <FormControl
                                            fullWidth
                                            error={showError}
                                            variant="standard"
                                            sx={COMMON_STYLES.formControl}
                                        >
                                            <MFTextField
                                                name={namePath}
                                                value={getValue(r.key, c.key)}
                                                onChange={handleChange(r.key, c.key)}
                                                size="small"
                                                fullWidth
                                                placeholder={c.label}
                                                error={showError}
                                                inputProps={isTotalRow ? { readOnly: true } : undefined}
                                                sx={isTotalRow ? COMMON_STYLES.totalRowField : {}}
                                            />
                                        </FormControl>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Grid>
            )}
            <RemarksDialog
                open={remarksOpen}
                onClose={closeRemarks}
                onOkay={onRemarksOkay}
                rowLabel={remarksRowLabel}
                remarksDraft={remarksDraft}
                setRemarksDraft={setRemarksDraft}
            />
        </>
    );
}


function ProtectionCoverage(): JSX.Element {
    const { values, setFieldValue, errors, touched } = useFormikContext<Values>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const getValue = (section: string, col: string) =>
        (values as any)?.clientInvInf?.protectionCoverage?.[section]?.[col] ?? "";

    const handleChange = (section: string, col: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const path = `clientInvInf.protectionCoverage.${section}.${col}`;
        setFieldValue(path, e.target.value);
    };

    const rows = [
        { key: "lifeInsurance", label: "Life Insurance" },
        { key: "generalInsurance", label: "General Insurance (Medical)" },
    ];

    const cols = [
        { key: "totalSumInsured", label: "Total Sum Assured / Coverage (Rs. in Lakhs)" },
        { key: "annualPremium", label: "Annual Premium (Rs. in Lakhs)" },
    ];

    return (
        <>
            {isMobile ? (
                <Box sx={{ marginTop: 2, width: '100%' }}>
                    {rows.map((r) => {
                        const fields = cols.map((c) => {
                            const namePath = `clientInvInf.protectionCoverage.${r.key}.${c.key}`;
                            const error = getIn(errors, namePath);
                            const wasTouched = getIn(touched, namePath);
                            const showError = Boolean(error && wasTouched);

                            return {
                                key: c.key,
                                label: c.label,
                                namePath,
                                value: getValue(r.key, c.key),
                                onChange: handleChange(r.key, c.key),
                                error: showError,
                                placeholder: c.label,
                            };
                        });

                        return (
                            <MobileInvestmentFormCard
                                key={r.key}
                                instrument={r.label}
                                fields={fields}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Grid container spacing={1} sx={{ marginTop: 1 }}>
                    <Grid container item xs={12} spacing={1} sx={COMMON_STYLES.header}>
                        <Grid item {...PROTECTION_GRID_CONFIG.instrument} sx={COMMON_STYLES.header}>
                            Instruments
                        </Grid>
                        {cols.map((c) => (
                            <Grid item {...PROTECTION_GRID_CONFIG.col} key={c.key} sx={COMMON_STYLES.header}>
                                {c.label}
                            </Grid>
                        ))}
                    </Grid>
                    {rows.map((r) => (
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={1}
                            key={r.key}
                            sx={COMMON_STYLES.row}
                        >
                            <Grid item {...PROTECTION_GRID_CONFIG.instrument} sx={COMMON_STYLES.instrumentCell}>
                                {r.label}
                            </Grid>

                            {cols.map((c) => {
                                const namePath = `clientInvInf.protectionCoverage.${r.key}.${c.key}`;
                                const error = getIn(errors, namePath);
                                const wasTouched = getIn(touched, namePath);
                                const showError = Boolean(error && wasTouched);

                                return (
                                    <Grid item {...PROTECTION_GRID_CONFIG.col} key={c.key} sx={COMMON_STYLES.cell}>
                                        <FormControl
                                            fullWidth
                                            error={showError}
                                            variant="standard"
                                            sx={COMMON_STYLES.formControl}
                                        >
                                            <MFTextField
                                                name={namePath}
                                                value={getValue(r.key, c.key)}
                                                onChange={handleChange(r.key, c.key)}
                                                size="small"
                                                fullWidth
                                                placeholder={c.label}
                                                error={showError}
                                            />
                                        </FormControl>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Grid>
            )}
        </>
    );
}
function Financing(): JSX.Element {
    const { values, setFieldValue, errors, touched } = useFormikContext<Values>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const rows = [
        { key: "homeLoan", label: "Home Loan" },
        { key: "loanAgainstSecurities", label: "Loan Against Securities" },
        { key: "homeEquity", label: "Home Equity (LAP)" },
        { key: "otherLoan", label: "Other" },
        { key: "totalLoan", label: "Total Amount" },
    ];

    const cols = [
        { key: "totalLoanAmount", label: "Total Loan Amount (Rs. in Lakhs)", numeric: true },
        { key: "totalOutstanding", label: "Total Outstanding (Rs. in Lakhs)", numeric: true },
        { key: "monthlyEmi", label: "Monthly EMI (Rs. in Lakhs)", numeric: true },
        { key: "remarks", label: "Remarks", numeric: false },
    ];


    const [remarksOpen, setRemarksOpen] = useState(false);
    const [remarksPath, setRemarksPath] = useState<string | null>(null);
    const [remarksRowLabel, setRemarksRowLabel] = useState<string>("");
    const [remarksDraft, setRemarksDraft] = useState<string>("");

    const getValue = (section: string, col: string) =>
        (values as any)?.clientInvInf?.financing?.[section]?.[col] ?? "";

    const handleChange = (section: string, col: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const path = `clientInvInf.financing.${section}.${col}`;
        setFieldValue(path, e.target.value);
    };

    const openRemarks = (sectionKey: string, rowLabel: string) => {
        const path = `clientInvInf.financing.${sectionKey}.remarks`;
        const current = getValue(sectionKey, "remarks") ?? "";
        setRemarksPath(path);
        setRemarksRowLabel(rowLabel);
        setRemarksDraft(current);
        setRemarksOpen(true);
    };

    const closeRemarks = () => {
        setRemarksOpen(false);
        setRemarksPath(null);
        setRemarksDraft("");
    };

    const onRemarksOkay = () => {
        if (remarksPath) {
            setFieldValue(remarksPath, remarksDraft);
        }
        closeRemarks();
    };

    // auto-calc totals with guard
    useEffect(() => {
        const fin = (values as any)?.clientInvInf?.financing;
        if (!fin) return;

        const numericCols = ["totalLoanAmount", "totalOutstanding", "monthlyEmi"];
        const sums: Record<string, number> = { totalLoanAmount: 0, totalOutstanding: 0, monthlyEmi: 0 };

        rows
            .filter((r) => r.key !== "totalLoan")
            .forEach((row) => {
                numericCols.forEach((col) => {
                    const raw = fin[row.key]?.[col];
                    const num = raw === "" || raw === null || raw === undefined ? 0 : Number(raw);
                    if (!isNaN(num)) sums[col] += num;
                });
            });

        const newTotalLoanAmount = sums.totalLoanAmount ? formatSumValue(sums.totalLoanAmount) : "";
        const newTotalOutstanding = sums.totalOutstanding ? formatSumValue(sums.totalOutstanding) : "";
        const newMonthlyEmi = sums.monthlyEmi ? formatSumValue(sums.monthlyEmi) : "";

        const currentTotalLoanAmount = fin?.totalLoan?.totalLoanAmount ?? "";
        const currentTotalOutstanding = fin?.totalLoan?.totalOutstanding ?? "";
        const currentMonthlyEmi = fin?.totalLoan?.monthlyEmi ?? "";

        if (currentTotalLoanAmount !== newTotalLoanAmount) {
            setFieldValue(
                "clientInvInf.financing.totalLoan.totalLoanAmount",
                newTotalLoanAmount
            );
        }
        if (currentTotalOutstanding !== newTotalOutstanding) {
            setFieldValue(
                "clientInvInf.financing.totalLoan.totalOutstanding",
                newTotalOutstanding
            );
        }
        if (currentMonthlyEmi !== newMonthlyEmi) {
            setFieldValue("clientInvInf.financing.totalLoan.monthlyEmi", newMonthlyEmi);
        }
    }, [
        (values as any)?.clientInvInf?.financing?.homeLoan?.totalLoanAmount,
        (values as any)?.clientInvInf?.financing?.loanAgainstSecurities?.totalLoanAmount,
        (values as any)?.clientInvInf?.financing?.homeEquity?.totalLoanAmount,
        (values as any)?.clientInvInf?.financing?.otherLoan?.totalLoanAmount,
        (values as any)?.clientInvInf?.financing?.homeLoan?.totalOutstanding,
        (values as any)?.clientInvInf?.financing?.loanAgainstSecurities?.totalOutstanding,
        (values as any)?.clientInvInf?.financing?.homeEquity?.totalOutstanding,
        (values as any)?.clientInvInf?.financing?.otherLoan?.totalOutstanding,
        (values as any)?.clientInvInf?.financing?.homeLoan?.monthlyEmi,
        (values as any)?.clientInvInf?.financing?.loanAgainstSecurities?.monthlyEmi,
        (values as any)?.clientInvInf?.financing?.homeEquity?.monthlyEmi,
        (values as any)?.clientInvInf?.financing?.otherLoan?.monthlyEmi,
        setFieldValue,
    ]);

    return (
        <>
            {isMobile ? (
                <Box sx={{ marginTop: 2, width: '100%' }}>
                    {rows.map((r) => {
                        const isTotalRow = r.key === "totalLoan";
                        const fields = cols
                            .filter((c) => c.key !== 'remarks')
                            .map((c) => {
                                const namePath = `clientInvInf.financing.${r.key}.${c.key}`;
                                const error = getIn(errors, namePath);
                                const wasTouched = getIn(touched, namePath);
                                const showError = Boolean(error && wasTouched);

                                return {
                                    key: c.key,
                                    label: c.label,
                                    namePath,
                                    value: getValue(r.key, c.key),
                                    onChange: handleChange(r.key, c.key),
                                    error: showError,
                                    numeric: c.numeric,
                                    readOnly: isTotalRow,
                                    placeholder: c.label,
                                };
                            });

                        const remarksValue = getValue(r.key, 'remarks') ?? '';
                        const remarksNamePath = `clientInvInf.financing.${r.key}.remarks`;
                        const remarksError = Boolean(getIn(errors, remarksNamePath) && getIn(touched, remarksNamePath));

                        return (
                            <MobileInvestmentFormCard
                                key={r.key}
                                instrument={r.label}
                                fields={fields}
                                remarks={remarksValue}
                                remarksError={remarksError}
                                onRemarksClick={() => openRemarks(r.key, r.label)}
                            />
                        );
                    })}
                </Box>
            ) : (
                <Grid container spacing={1} sx={{ marginTop: 1 }}>
                    {/* header */}
                    <Grid
                        container
                        item
                        xs={12}
                        spacing={1}
                        columnSpacing={DESKTOP_COLUMN_SPACING}
                        columns={COMMON_GRID_CONFIG.columns}
                        sx={COMMON_STYLES.header}
                    >
                        <Grid item {...COMMON_GRID_CONFIG.instrument} sx={COMMON_STYLES.header}>
                            Instruments
                        </Grid>
                        {cols.map((c) => (
                            <Grid
                                item
                                {...(c.key === "remarks" ? COMMON_GRID_CONFIG.remarks : COMMON_GRID_CONFIG.col)}
                                key={c.key}
                                sx={{
                                    ...COMMON_STYLES.header,
                                    ...(c.key === "remarks" ? COMMON_STYLES.remarksHeader : {}),
                                }}
                            >
                                {c.label}
                            </Grid>
                        ))}
                    </Grid>
                    {rows.map((r) => (
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={1}
                            columnSpacing={DESKTOP_COLUMN_SPACING}
                            columns={COMMON_GRID_CONFIG.columns}
                            key={r.key}
                            sx={COMMON_STYLES.row}
                        >
                            <Grid item {...COMMON_GRID_CONFIG.instrument} sx={COMMON_STYLES.instrumentCell}>
                                {r.label}
                            </Grid>

                            {cols.map((c) => {
                                const namePath = `clientInvInf.financing.${r.key}.${c.key}`;
                                const error = getIn(errors, namePath);
                                const wasTouched = getIn(touched, namePath);
                                const showError = Boolean(error && wasTouched);

                                const isTotalRow = r.key === "totalLoan";
                                const isRemarksCol = c.key === "remarks";

                                if (isRemarksCol) {
                                    const currentRemark = getValue(r.key, c.key) ?? "";
                                    const hasRemark = Boolean(currentRemark.trim());

                                    return (
                                        <Grid
                                            item
                                            {...COMMON_GRID_CONFIG.remarks}
                                            key={c.key}
                                            sx={COMMON_STYLES.remarksCell}
                                        >
                                            <Tooltip
                                                title={hasRemark ? "Edit remark" : "Add a remark"}
                                                placement="top"
                                                arrow
                                            >
                                                <span>
                                                    <IconButton
                                                        aria-label={hasRemark ? `edit-remark-${r.key}` : `add-remark-${r.key}`}
                                                        size="small"
                                                        onClick={() => openRemarks(r.key, r.label)}
                                                        sx={{
                                                            ...COMMON_STYLES.remarksButton,
                                                            ...(hasRemark ? COMMON_STYLES.remarksButtonHasValue : {})
                                                        }}
                                                    >
                                                        {hasRemark ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Grid>
                                    );
                                }
                                if (c.numeric) {
                                    return (
                                        <Grid
                                            item
                                            {...COMMON_GRID_CONFIG.col}
                                            key={c.key}
                                            sx={COMMON_STYLES.cell}
                                        >
                                            <FormControl
                                                fullWidth
                                                error={showError}
                                                variant="standard"
                                                sx={COMMON_STYLES.formControl}
                                            >
                                                <MFTextField
                                                    name={namePath}
                                                    value={getValue(r.key, c.key)}
                                                    onChange={handleChange(r.key, c.key)}
                                                    size="small"
                                                    fullWidth
                                                    placeholder={c.label}
                                                    error={showError}
                                                    inputProps={isTotalRow ? { readOnly: true } : { inputMode: "numeric", pattern: "[0-9]*" }}
                                                    sx={isTotalRow ? COMMON_STYLES.totalRowField : {}}
                                                />
                                            </FormControl>
                                        </Grid>
                                    );
                                }

                                return (
                                    <Grid
                                        item
                                        {...(c.key === "remarks" ? COMMON_GRID_CONFIG.remarks : COMMON_GRID_CONFIG.col)}
                                        key={c.key}
                                        sx={COMMON_STYLES.cell}
                                    >
                                        <FormControl
                                            fullWidth
                                            error={showError}
                                            variant="standard"
                                            sx={COMMON_STYLES.formControl}
                                        >
                                            <MFTextField
                                                name={namePath}
                                                value={getValue(r.key, c.key)}
                                                onChange={handleChange(r.key, c.key)}
                                                size="small"
                                                fullWidth
                                                placeholder={c.label}
                                                error={showError}
                                                inputProps={isTotalRow ? { readOnly: true } : undefined}
                                                sx={isTotalRow ? COMMON_STYLES.totalRowField : {}}
                                            />
                                        </FormControl>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Grid>
            )}
            <RemarksDialog
                open={remarksOpen}
                onClose={closeRemarks}
                onOkay={onRemarksOkay}
                rowLabel={remarksRowLabel}
                remarksDraft={remarksDraft}
                setRemarksDraft={setRemarksDraft}
            />
        </>
    );
}
interface RemarksDialogProps {
    open: boolean;
    onClose: () => void;
    onOkay: () => void;
    rowLabel: string;
    remarksDraft: string;
    setRemarksDraft: (value: string) => void;
}

function RemarksDialog({
    open,
    onClose,
    onOkay,
    rowLabel,
    remarksDraft,
    setRemarksDraft
}: RemarksDialogProps): JSX.Element {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    maxHeight: "60vh",
                    paddingY: 1,
                },
            }}
        >
            <Box sx={{ pt: 1, pb: 0 }}>
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        lineHeight: 1.2,
                        paddingBottom: 0.2,
                    }}
                >
                    Remarks (IF ANY)
                </DialogTitle>
                <DialogTitle
                    sx={{
                        fontSize: "0.98rem",
                        color: "text.primary",
                        paddingTop: 0,
                        marginBottom: 1,
                    }}
                >
                    {rowLabel}
                </DialogTitle>
            </Box>

            <DialogContent sx={{ px: 3, py: 0 }}>
                <TextField
                    autoFocus
                    multiline
                    minRows={4}
                    fullWidth
                    placeholder="Enter"
                    value={remarksDraft}
                    onChange={(e) => setRemarksDraft(e.target.value)}
                    variant="outlined"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                        },
                    }}
                />
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button
                    variant="contained"
                    onClick={onOkay}
                    fullWidth
                    sx={{
                        borderRadius: "10px",
                        fontWeight: 600,
                        height: 40,
                    }}
                >
                    Okay
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const clientInvInfObject = [
    { id: 1, name: 'Investment Portfolio', description: 'Please describe your current Investment Portfolio', component: InvestmentPortfolio },
    { id: 2, name: 'Protection Coverage', description: 'Please describe your current Protection Coverage (Life/Non-Life Insurance cover)', component: ProtectionCoverage },
    { id: 3, name: 'Financing', description: 'Please share your current Financing details (Home Loan, Other retail asset loans)', component: Financing },
];

interface ClientInvInfProps {
    isViewProfile?: boolean;
    onClose?: () => void;
    onSuccess?: () => Promise<void> | void;
    applicationId?: string | number;
    applicationData?: any; //view profiledata
}

export default function clientInvInfrmation({
    isViewProfile = false,
    onClose,
    onSuccess,
    applicationId: propApplicationId,
    applicationData
}: ClientInvInfProps = {}): JSX.Element {
    const theme = useTheme();
    const formikRef = useRef<FormikProps<Values>>(null);
    const history = useHistory();
    const dispatch = useDispatch();
    const { application: reduxApplication } = useSelector((store: RootStateType) => store.application);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [clientInvInfData, setclientInvInfData] = useState(initialValues);
    const { path: currentPath } = useRouteMatch();
    const isDiyInvestorOnboarding = currentPath?.includes("/investor-onboarding-application");

    // route regarding view profile
    const isViewProfileRoute = isViewProfile || history.location.pathname.includes('/investor-dashboard/view-profile');

    const isViewProfilePage = history.location.pathname.includes('view-profile');

    //application data for view profile else fallback  backstore
    const application = applicationData || reduxApplication;

    //backend array to frontned object
    const transformBackendToFrontend = (backendData: any): clientInvInf => {
        if (!backendData) return clientInvInfInitialValues;
        if (backendData.investmentPortfolio && typeof backendData.investmentPortfolio === 'object' && !Array.isArray(backendData.investmentPortfolio)) {
            return backendData as clientInvInf;
        }

        const TYPE_TO_KEY_MAP: Record<string, string> = {
            'Mutual Fund': 'mutualFund',
            'Fixed Deposit': 'fixedDeposit',
            'Bond': 'bond',
            'Direct Equity': 'directEquity',
            'PMS': 'pms',
            'AIF': 'aif',
            'Real Estate': 'realEstate',
            'Other Investment': 'otherInvestment',
            'Total Investment': 'totalInvestment',
            'Home Loan': 'homeLoan',
            'Loan against securities': 'loanAgainstSecurities',
            'Loan Against Securities': 'loanAgainstSecurities',
            'Home Equity': 'homeEquity',
            'Other Loan': 'otherLoan',
            'Total Loan': 'totalLoan',
            'Life Insurance': 'lifeInsurance',
            'General Insurance': 'generalInsurance',
        };

        const result: any = {
            investmentPortfolio: {},
            protectionCoverage: {},
            financing: {},
        };
        if (Array.isArray(backendData.investmentPortfolio)) {
            backendData.investmentPortfolio.forEach((item: any) => {
                const key = TYPE_TO_KEY_MAP[item.type];
                if (key) {
                    result.investmentPortfolio[key] = {
                        totalInvestment: item.totalInvestment || '',
                        investmentThroughTCW: item.investmentThroughTcw || item.investmentThroughTCW || '',
                        otherWealthBank: item.otherWealthBank || '',
                        remarks: item.remarks || '',
                    };
                }
            });
        }
        if (Array.isArray(backendData.protectionCoverage)) {
            backendData.protectionCoverage.forEach((item: any) => {
                const key = TYPE_TO_KEY_MAP[item.type];
                if (key) {
                    result.protectionCoverage[key] = {
                        totalSumInsured: item.totalSumInsured || '',
                        annualPremium: item.annualPremium || '',
                    };
                }
            });
        }
        if (Array.isArray(backendData.financing)) {
            backendData.financing.forEach((item: any) => {
                const key = TYPE_TO_KEY_MAP[item.type];
                if (key) {
                    result.financing[key] = {
                        totalLoanAmount: item.totalLoanAmount || '',
                        totalOutstanding: item.totalOutstanding || '',
                        monthlyEmi: item.monthlyEmi || '',
                        remarks: item.remarks || '',
                    };
                }
            });
        }

        return result as clientInvInf;
    };

    useEffect(() => {
        if (Object.keys(application ?? {}).length > 0) {
            const backendData = (application as any)?.clientInvestmentInfo;
            let prefilledclientInvInf: clientInvInf = clientInvInfInitialValues;

            if (backendData) {
                prefilledclientInvInf = transformBackendToFrontend(backendData);
            } else {
                const applicationLevelclientInvInf = application?.clientInvInf as clientInvInf | undefined;
                const applicantLevelclientInvInf = application?.applicants?.[0]?.clientInvInf as clientInvInf | undefined;
                const rawData = applicationLevelclientInvInf || applicantLevelclientInvInf;
                prefilledclientInvInf = rawData ? transformBackendToFrontend(rawData) : clientInvInfInitialValues;
            }

            setclientInvInfData({
                status: application?.status || '',
                applicationType: application?.applicationType || '',
                clientInvInf: prefilledclientInvInf,
                saveType: 'save and proceed',
            });
        }
    }, [application]);

    const handleSubmit = async (values: Values) => {
        try {
            const { clientInvInf } = values;
            //clientinvestment update in view profile api
            if (isViewProfileRoute) {
                const appId = propApplicationId || (application as any)?.id || (application as any)?.applicationId || application?.id;

                if (!appId) {
                    dispatch(showError('Application ID is missing'));
                    return;
                }

                setLoading(true);
                const response = (await dispatch(updateClientInvestment({
                    applicationId: Number(appId),
                    clientInvInf,
                }))) as any;

                if (!response) {
                    setLoading(false);
                    return;
                }

                if (response.isSuccess) {
                    enqueueSnackbar(response.message || 'Client Investment Information updated successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                    if (onSuccess) {
                        await onSuccess();
                    }
                    if (onClose) {
                        onClose();
                    }
                } else {
                    dispatch(showError(response.message || 'Failed to update Client Investment Information'));
                }

                setLoading(false);
                return;
            }
            const {
                id,
                applicant1ReferenceId = '',
                currentStep,
                status,
                applicationNumber,
            } = application || {};
            const { saveType } = values;

            if (!id) {
                dispatch(showError('Application ID is missing'));
                return;
            }

            const isSaveLater = saveType !== 'save and proceed';
            const targetCurrentStep = ONBOARDING_DEMAT_DETAILS ? 11 : 10;
            const comparisonCurrentStep = !!currentStep && currentStep > targetCurrentStep ? currentStep : Number(currentStep ?? 0) + 1;

            const updatedApplication = {
                ...application,
                clientInvInf,
                status: sendRoleBasedStatus(role, status as string),
                currentStep: targetCurrentStep,
            };

            const checkApplication = applicationComparison(
                application,
                {
                    ...application,
                    clientInvInf,
                    currentStep: comparisonCurrentStep,
                }
            );

            const ClientInvestementPayload = {
                body: updatedApplication,
                applicationId: id,
                ...(isSaveLater && { toastMessage: '' }),
            };

            // console.log('Client Investment Information (clientInvInf):', clientInvInf);

            if (id && !checkApplication) {
                setLoading(true);
                const response = await dispatch(updateApplication(ClientInvestementPayload));

                if (!response) {
                    setLoading(false);
                    return;
                }

                if (!isSaveLater) {
                    history.push('document-details', { id, applicant1ReferenceId });
                } else {
                    history.push(saveForLater(role, id, applicant1ReferenceId, isDiyInvestorOnboarding));
                }
            } else if (checkApplication) {
                if (isSaveLater) {
                    enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                }

                if (!isSaveLater) {
                    history.push('document-details', { id, applicant1ReferenceId });
                } else {
                    history.push(saveForLater(role, id, applicant1ReferenceId, isDiyInvestorOnboarding));
                }
            }
        } catch (e) {
            setLoading(false);
            typeof e === 'string' && dispatch(showError(e));
            console.error((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = async (values: Values) => {
        try {
            setLoading(true);
            const { id, applicant1ReferenceId = '' } = application;
            const { clientInvInf } = values;
            const updatedApplication = {
                ...application,
                // clientInvInf,
                status: sendRoleBasedStatus(role, status as string),
                currentStep: ONBOARDING_DEMAT_DETAILS ? 11 : 10
            };
            const ClientInvestementPayload = {
                body: updatedApplication,
                applicationId: id,
                // ...(isSaveLater && { toastMessage: '' }),
            };
            const response = await dispatch(updateApplication(ClientInvestementPayload));

            if (!response) {
                setLoading(false);
                return;
            }
            history.push('document-details', { id, applicant1ReferenceId });

        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Formik
            innerRef={formikRef}
            initialValues={clientInvInfData}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={clientInvInfrmationValidationSchema}
            validateOnChange={false} // avoid render in every key preses
            validateOnBlur={true}
        >
            {({ handleSubmit, values, setValues }) => (
                <Box component="form" noValidate onSubmit={handleSubmit}>
                    <Grid container item xs={12}>
                        {!isViewProfilePage &&
                            <Grid item xs={6} md={3}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={() => handleSkip(values)}
                                    sx={{
                                        mr: 1,
                                        textTransform: "none",
                                    }}
                                    loading={loading}
                                >
                                    Skip
                                </LoadingButton>
                            </Grid>}
                        {clientInvInfObject.map((item) => {
                            const Component = item.component;
                            return (
                                <Grid item xs={12} key={item.id}>
                                    <SubHeading
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            fontFamily: `${theme.typography.fontFamily}`,
                                        }}
                                    >
                                        {item.id}. {item.description}
                                    </SubHeading>
                                    <Component />
                                </Grid>
                            );
                        })}
                        <Grid container spacing={2}>
                            <Grid item xs={12} my={1} />
                            <ProceedSaveLater
                                saveLater={() => {
                                    setValues({
                                        ...values,
                                        saveType: 'save for later',
                                    });
                                }}
                                saveAndProceed={() => {
                                    setValues({
                                        ...values,
                                        saveType: 'save and proceed',
                                    });
                                }}
                                loader={loading}
                                clickedButton={values.saveType}
                                saveButtonText={isViewProfileRoute ? "Proceed" : "Continue"}
                                showEndIcon={false}
                                showSaveForLater={!isViewProfileRoute}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Formik>
    );
}